import { useContext, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useToggle } from "react-use";
import { Video } from "../../mock/video-wall";
import { fill } from "../../styles/utils";
import { CursorHolder } from "../CursorHolder";
import { VideoWallCursor } from "../cursors/VideoWallCursor";
import { VideoWallBoard } from "./VideoWallBoard";
import { VideoWallVideo } from "./VideoWallVideo";
import { VideoWallPlayer } from "./VideoWallPlayer";
import { VideoWallContext } from "../../context/VideoWallContext";
import { getVideoWall } from "../../api/api";
import { Box } from "../Box";

const Wrapper = styled.div`
  ${fill("fixed")}
  background-color: ${({ theme }) => theme.colors.blueDark};
  color: ${({ theme }) => theme.colors.white};
  cursor: none;
`;

const VideoPosition = styled.div<{ x: number; y: number }>`
  position: absolute;
  width: 30vw;

  ${({ x, y }) => css`
    top: ${y}%;
    left: ${x}%;
  `}
`;

const videosPresenceVariants: Variants = {
  hidden: (isSelected) =>
    isSelected
      ? { opacity: 0, transition: { duration: 0.2 } }
      : { opacity: 0, transition: { delay: 0.4, duration: 1.2 } },
  active: { opacity: 1, transition: { delay: 1, duration: 1.2 } },
};

export default function VideoWall() {
  const [selected, setSelected] = useState(false);
  const [stoppedMoving, setStoppedMoving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandCursor, setExpandCursor] = useToggle(false);
  const [playerReady, setPlayerReady] = useToggle(false);

  const { videoWallConfig, setVideoWallConfig } = useContext(VideoWallContext);

  useEffect(() => {
    getVideoWall().then((v) => {
      if (!videoWallConfig && v) {
        console.log(setVideoWallConfig);
        setVideoWallConfig(v);
      }
    });
  }, [videoWallConfig, setVideoWallConfig]);

  const [{ selectedVideo, currentTime, domRect }, setVideo] = useState<{
    selectedVideo: Video | null;
    currentTime: number | null;
    domRect: DOMRect | null;
  }>({ selectedVideo: null, currentTime: null, domRect: null });

  const handleVideoMouseOver = () => {
    setExpandCursor(true);
  };

  const handleVideoMouseLeave = () => {
    setExpandCursor(false);
  };

  const handleVideoMouseDown = () => {
    setExpandCursor(true);
  };

  const handleCloseVideoClick = () => {
    setVideo({ selectedVideo: null, currentTime: null, domRect: null });
    setSelected(false);
    setPlayerReady(false);
  };

  const handleOnPlayerReady = () => {
    // setPlayerReady(true);
  };

  const handleOnPlay = () => {
    setPlayerReady(true);
  };

  const handleStopAnimating = (v: boolean) => {
    if (v && selected) {
      setStoppedMoving(v);
    }
    if (!selected && !v) {
      setStoppedMoving(v);
    }
  };

  useEffect(() => {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    setIsMobile(isMobile);
  }, []);

  const handleVideoClick = (
    video: Video,
    currentTime: number,
    domRect: DOMRect
  ) => {
    // const target = e.target as HTMLElement;
    // console.log(target.getBoundingClientRect());
    setVideo({
      selectedVideo: video,
      currentTime,
      domRect,
    });

    setTimeout(() => {
      setSelected(true);
    }, 100);
  };

  return (
    <Wrapper>
      {!isMobile && (
        <CursorHolder visible={true}>
          <VideoWallCursor expanded={expandCursor} />
        </CursorHolder>
      )}

      <VideoWallBoard
        isMobile={isMobile}
        watchMouseMove={!selected}
        onStopAnimating={handleStopAnimating}
      >
        {videoWallConfig &&
          videoWallConfig.videos.length &&
          videoWallConfig.videos.map((video) => (
            <motion.div
              key={video.id}
              animate={selected && stoppedMoving ? "hidden" : "active"}
              custom={
                selectedVideo
                  ? selectedVideo.id === video.id && playerReady
                  : false
              }
              variants={videosPresenceVariants}
            >
              <VideoPosition
                key={video.id}
                x={video.position.x}
                y={video.position.y}
              >
                <VideoWallVideo
                  video={video}
                  selected={
                    selectedVideo ? selectedVideo.id === video.id : false
                  }
                  onMouseOver={handleVideoMouseOver}
                  onMouseLeave={handleVideoMouseLeave}
                  onMouseDown={handleVideoMouseDown}
                  onClick={(currentTime, domRect) =>
                    handleVideoClick(video, currentTime, domRect)
                  }
                ></VideoWallVideo>
              </VideoPosition>
            </motion.div>
          ))}
      </VideoWallBoard>
      <AnimatePresence initial={false}>
        {selectedVideo && currentTime && (
          <Box opacity={playerReady ? 1 : 0}>
            <VideoWallPlayer
              video={selectedVideo}
              domRect={domRect}
              startAt={currentTime || 0}
              onCloseClick={handleCloseVideoClick}
              onReady={handleOnPlayerReady}
              onPlay={handleOnPlay}
              show={stoppedMoving}
            />
          </Box>
        )}
      </AnimatePresence>
    </Wrapper>
  );
}
